'use client';

import type { FragmentType } from 'src/gql';
import IconButton from '@savvy-kit/components/icon-button';
import { Text } from '@savvy-kit/foundations/text';
import { isNil } from 'es-toolkit';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { graphql, unmaskFragment } from 'src/gql';

import { PageSizeSelect } from '../../pagination/page-size-select';
import { useTaxOverviewParams } from './tax-overview-url-state';

export const TaxOverviewPaginationFooterFragment = graphql(/* GraphQL */ `
  fragment TaxOverviewPaginationFooter on TaxEnrollmentsTableRowConnection {
    edges {
      node {
        taxEnrollmentId
      }
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
    totalCount
  }
`);

type Props = {
  readonly queryFrag: FragmentType<typeof TaxOverviewPaginationFooterFragment> | undefined;
};

export function TaxOverviewPaginationFooter({ queryFrag }: Props) {
  const { params, setPageSize, setAfter, setBefore } = useTaxOverviewParams();

  const data = unmaskFragment(TaxOverviewPaginationFooterFragment, queryFrag);

  if (!data) {
    return null;
  }

  const totalItems = Number(data.totalCount);
  const { hasNextPage, hasPreviousPage, startCursor, endCursor } = data.pageInfo;

  const startCursorValue = !isNil(startCursor)
    ? Number(Buffer.from(startCursor, 'base64').toString('utf-8'))
    : undefined;
  const endCursorValue = !isNil(endCursor) ? Number(Buffer.from(endCursor, 'base64').toString('utf-8')) : undefined;

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      setBefore(startCursor);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      setAfter(endCursor);
    }
  };

  const displayText = `${startCursorValue} - ${endCursorValue} of ${totalItems}`;

  return (
    <div className="flex flex-row gap-4 items-center justify-end p-2 border-t border-t-border-light">
      <Text variant="caption" color="secondary">
        Rows per page:
      </Text>
      <PageSizeSelect value={params.pageSize} onChange={handlePageSizeChange} />
      <div className="flex flex-row gap-2 items-center">
        <Text variant="caption" color="secondary">
          {displayText}
        </Text>
        <IconButton
          variant="ghost"
          size="sm"
          aria-label="Previous page"
          onClick={handlePreviousPage}
          disabled={!hasPreviousPage}
        >
          <ChevronLeft />
        </IconButton>
        <IconButton variant="ghost" size="sm" aria-label="Next page" onClick={handleNextPage} disabled={!hasNextPage}>
          <ChevronRight />
        </IconButton>
      </div>
    </div>
  );
}
